import {
  BacteriaClassification,
  ReportSummary,
  KeyBacteria,
  BacteriaAttentionAction,
  BacteriaAttentionCategory,
  BacteriaResearchSummary,
  BacteriaReport,
} from '@snapi/types';
import { ReportComparatorUtils } from './report-comparator-utils';

const extract = (bacteria: BacteriaReport[]): KeyBacteria[] =>
  bacteria
    .filter(
      ({ threshold: { classification } }) =>
        classification === BacteriaClassification.key
    )
    .map<KeyBacteria>(
      ({
        percentage,
        description,
        threshold: {
          research: { name, taxonomy },
          threshold,
          comparator,
        },
      }) => {
        return {
          name,
          taxonomy,
          percentage,
          threshold,
          comparator,
          description,
          category: BacteriaAttentionCategory.key,
          action: ReportComparatorUtils.passesThreshold(
            percentage,
            comparator,
            threshold
          )
            ? BacteriaAttentionAction.maintain
            : BacteriaAttentionAction.increase,
        };
      }
    )
    .sort((a, b) => b.percentage / b.threshold - a.percentage / a.threshold);

const extractSummary = (
  bacteria: string,
  thresholds: BacteriaReport[]
): BacteriaResearchSummary | null => {
  const threshold = thresholds.find(
    ({
      threshold: {
        research: { name },
      },
    }) => name === bacteria
  );

  if (!threshold) {
    return null;
  }

  return {
    bacteria: {
      research: threshold.threshold.research,
      percentage: 0,
      classified: 0,
      other: 0,
      unclassified: 0,
      protective: 0,
      disruptive: 0,
      neutral: 0,
      description: threshold.description
    },
    species: [],
  };
};

const isGood = (bacteria: Pick<KeyBacteria, 'action'>[]): boolean =>
  bacteria.filter(({ action }) => action !== BacteriaAttentionAction.maintain)
    .length <= 1;

const summary = (bacteria: BacteriaReport[]): ReportSummary['keyBacteria'] => {
  const key = extract(bacteria);

  return {
    bacteria: key.slice(0, 3),
    good: isGood(key),
  };
};

export const ReportKeyBacteriaUtils = {
  isGood,
  extract,
  extractSummary,
  summary,
};
